<template>
  <section class="user-details ine-card">
    <div class="user-details__card">
      <div class="user-details__avatar-wrapper">
        <ProfileProgress
          class="user-details__avatar"
          :currentAvatar="currentAvatar"
          :onboardingItems="onboardingItems"
        />
        <p class="user-details__user-name">{{ userName }}</p>
        <p class="user-details__info">{{ email }}</p>
      </div>
      <div class="user-details__details-wrapper">
        <div class="user-details__profile-progess">
          {{ profileProgressLabel }}
        </div>
        <div
          v-for="(profileItem, key) in profileItems"
          :key="`item-${key}`"
          class="user-details__details-item"
          @click="openOnboardingItem(profileItem.step)"
        >
          <span
            :class="[
              'user-details__item-checkbox-control',
              isOnboardingItemAvailable(key) &&
                'user-details__item-checkbox-control--selected',
            ]"
          >
            <CheckIcon
              class="user-details__item-checkbox-icon"
              v-if="isOnboardingItemAvailable(key)"
            />
          </span>
          <div class="user-details__item-title">{{ profileItem.title }}</div>
          <div class="user-details__item-icons-wrapper">
            <Edit2Icon
              v-if="isOnboardingItemAvailable(key)"
              class="user-details__item-icon"
            />
            <ChevronRightIcon v-else class="user-details__item-icon" />
          </div>
        </div>
      </div>
    </div>
    <div
      class="btn btn--promo user-details__upgrade-label"
      @click="handleUpgradeAccount"
    >
      Upgrade your account
    </div>
    <UpgradePlanModal
      v-if="isUpgradeModalShown"
      :subscriptionId="activePlans[0].id"
      @toggle-upgrade-modal="toggleUpgradeModal"
    />
  </section>
</template>

<script>
import CONSTANTS from '@/constants'
import { mapState, mapGetters } from 'vuex'
import ProfileProgress from '@/components/navbar/ProfileProgress.vue'
import UpgradePlanModal from '@/components/modals/UpgradePlanModal.vue'
import { ChevronRightIcon, CheckIcon, Edit2Icon } from 'vue-feather-icons'

export default {
  name: 'UserDetails',
  components: {
    ProfileProgress,
    UpgradePlanModal,
    CheckIcon,
    ChevronRightIcon,
    Edit2Icon,
  },
  props: {
    currentAvatar: {
      type: String,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    userInfo: {
      type: Object,
      required: true,
    },
    canManageMFA: {
      type: Boolean,
      required: true,
    },
    isBusinessUser: {
      type: Boolean,
      required: true,
    },
    canViewPaymentInfo: {
      type: Boolean,
      required: true,
    },
    paymentInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      onboardingStep: CONSTANTS.PROFILE.ONBOARDING.STEPS.PROFILE_PHOTO,
      isUpgradeModalShown: false,
      profileItems: {
        avatar: {
          id: 'avatar',
          title: 'Add your profile photo',
          checked: false,
          step: CONSTANTS.PROFILE.ONBOARDING.STEPS.PROFILE_PHOTO,
        },
        learning_areas: {
          id: 'learning_areas',
          title: 'Add your Learning Area',
          checked: false,
          step: CONSTANTS.PROFILE.ONBOARDING.STEPS.INTEREST_AREA,
        },
        backup_email: {
          id: 'backup_email',
          title: 'Add backup email',
          checked: false,
          step: CONSTANTS.PROFILE.ONBOARDING.STEPS.BACKUP_EMAIL,
        },
        birthday: {
          id: 'birthday',
          title: 'Add your Birthday',
          checked: false,
          step: CONSTANTS.PROFILE.ONBOARDING.STEPS.BIRTHDAY,
        },
      },
    }
  },
  computed: {
    ...mapState({
      onboardingItems: state => state.auth.onboardingSurvey,
      onboardingModalStatus: state => state.app.onboardingModalStatus,
      plansLink: state => state.app.plansLink,
    }),
    ...mapGetters({
      usersSubscriptions: 'subscriptions/usersSubscriptions',
    }),
    email() {
      const { email } = this.userInfo
      return email
    },
    phone() {
      const { phoneNumber: phone } = this.userInfo
      return phone || 'None'
    },
    mfaMethod() {
      const { mfaMethod } = this.userInfo
      return mfaMethod || 'None'
    },
    isEditMFAButtonShown() {
      if (this.isBusinessUser && !this.canManageMFA) {
        return false
      }
      return true
    },
    paymentMethod() {
      const {
        paymentInfo: { payment_method },
      } = this
      return payment_method
    },
    isAPayPalCustomer() {
      return (
        this.paymentMethod &&
        this.paymentMethod === CONSTANTS.SUBSCRIPTIONS.PAYPAL_PAYMENT_TYPE
      )
    },
    last4OfCC() {
      const {
        paymentInfo: { last4_of_cc },
      } = this
      return last4_of_cc
    },
    profileProgressLabel() {
      return `PROFILE: ${this.onboardingItems.completedPercentage}% COMPLETE`
    },
    activePlans() {
      const {
        usersSubscriptions: { activePlans, failedPlans },
      } = this
      return [...activePlans, ...failedPlans]
    },
  },
  methods: {
    isOnboardingItemAvailable(key) {
      return this.onboardingItems.data && this.onboardingItems.data[key]
    },
    openOnboardingModal(step = this.onboardingStep) {
      this.$store.dispatch('app/setOnboardingModalStatus', {
        opened: true,
        step: step,
      })
    },
    openOnboardingItem(step) {
      this.openOnboardingModal(step)
    },
    toggleUpgradeModal() {
      this.isUpgradeModalShown = !this.isUpgradeModalShown
    },
    handleUpgradeAccount() {
      if (this.activePlans?.length) {
        this.toggleUpgradeModal()
      } else {
        window.open(`https://${this.plansLink}`, '_blank')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.user-details {
  display: flex;
  flex-direction: column;
  border-radius: 0 0 10px;
  margin: 0 0 55px 0;
  width: 100%;
  @include tablet {
    border-radius: 10px;
    width: 550px;
    margin: 55px auto;
  }
  @include desktop {
    margin: 0 50px 55px 0;
    height: 615px;
  }
  &__card {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 570px;
    position: relative;
    padding: 25px 40px 40px 40px;
    @include mobile {
      min-width: 390px;
    }
  }
  &__avatar-wrapper {
    text-align: center;
    padding: 0 0 20px;
  }
  &__avatar {
    display: flex;
    height: 120px;
    width: 120px;
    margin: 0 auto 15px;
    border-radius: 100%;
    margin-bottom: 15px;
  }
  &__user-name {
    color: var(--white);
    font-size: rem(20px);
    font-weight: 700;
    line-height: 26px;
    padding: 0;
    margin-bottom: 10px;
  }
  &__info {
    font-size: rem(12px);
    line-height: 14px;
    color: var(--bright-gray);
  }
  &__details-wrapper {
    margin-top: auto;
    color: var(--white);
    @include mobile {
      padding: 0px 50px 25px 50px;
      margin: 0 0 35px;
    }
    @include tablet {
      padding: 0;
    }
  }
  &__profile-progess {
    width: fit-content;
    font-size: rem(12);
    line-height: 14px;
    padding: 9px 14px;
    background: var(--glass-mid-gray);
    border: 1px solid var(--tundora-gray);
    border-radius: 20px;
    margin: 0 auto 15px;
  }
  &__details-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 5px 5px 5px 10px;
    flex-wrap: wrap;
    background: var(--glass-mid-gray);
    margin-bottom: 8px;
    border-radius: 5px;
    height: 50px;
    cursor: pointer;
    &:hover,
    &:active {
      background: var(--glass-extra-light);
      outline: solid 1px var(--mulled-wine-50) !important;
      .user-details__item-icons-wrapper {
        background: var(--white-10);
      }
    }
    &:active {
      background: transparent;
    }
  }
  &__item {
    &-title {
      margin-left: 10px;
      font-size: rem(14);
      line-height: 20px;
    }
    &-checkbox-icon {
      width: 14px;
      color: var(--tundora-gray);
      // margin-top: 2px;
    }
    &-checkbox-control {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 1px solid var(--light-blue-gray);
      &--selected {
        border: none;
        background: var(--positive-light);
      }
    }
    &-icons-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      margin-left: auto;
      display: flex;
      width: 40px;
      height: 40px;
      border-radius: 8px;
    }
    &-icon {
      height: 20px;
      width: 20px;
    }
  }
  &__action-btn {
    background-color: transparent;
    border: none;
    color: var(--white);
    margin: 0 0 0 auto;
    padding: 0 0 0 15px;
    cursor: pointer;
    transition: 0.5s all ease;
    text-decoration: underline;
    &:hover {
      color: var(--orange);
      border-color: var(--orange);
    }
  }
  &__dashboard-btn-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--flat-black);
    height: 66px;
  }
  &__dashboard-btn {
    color: var(--white);
    display: inline-block;
    transition: 0.5s all ease;
    font-size: 16px;
    &:hover {
      color: var(--orange);
      border-color: var(--orange);
    }
  }
  &__mfa-method {
    text-transform: uppercase;
  }
  .lead {
    font-size: rem(16px);
    color: var(--bright-gray);
  }
  .data {
    font-size: rem(16px);
    text-transform: none;
  }
  .material-icons {
    margin: 0 0 0 10px;
    font-size: 20px;
  }
  &__upgrade-label {
    font-size: rem(12);
    line-height: 15px;
    text-align: center;
    padding: 15px;
    border-radius: 0px 0px 10px 10px;
    cursor: pointer;
  }
}
</style>
